import React from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import jaLocale from '@fullcalendar/core/locales/ja';
import { Box, IconButton } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';

import { scheduleEventColors } from '../../utils/colors';
import { 
  createDateClickHandler,
  createEventClickHandler,
  createEventDropHandler
} from '../../utils/eventHandlers';
import {
  createViewChangeHandler,
  createEventContentRenderer,
  filterEvents
} from '../../utils/eventHelpers';

const CalendarMain = ({ isMobile, isTablet, calendarState, calendarEvents, currentUser }) => {
  const isMobileOrTablet = isMobile || isTablet;
  const previousViewTypeRef = React.useRef(null);

  const isDateOnlyValue = (value) => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);

  const toTimestamp = (value, fallback = Number.MAX_SAFE_INTEGER) => {
    if (!value) return fallback;
    const date = value instanceof Date ? value : new Date(value);
    const time = date.getTime();
    return Number.isNaN(time) ? fallback : time;
  };

  const getCreatedAtValue = (event) => (
    event?.createdAt ||
    event?.created_at ||
    event?.extendedProps?.createdAt ||
    event?.extendedProps?.created_at ||
    ''
  );

  const compareByStartThenCreated = (a, b) => {
    const aStart = toTimestamp(a?.start);
    const bStart = toTimestamp(b?.start);
    if (aStart !== bStart) return aStart - bStart;

    const aCreated = toTimestamp(getCreatedAtValue(a));
    const bCreated = toTimestamp(getCreatedAtValue(b));
    if (aCreated !== bCreated) return aCreated - bCreated;

    const aId = String(a?.id || '');
    const bId = String(b?.id || '');
    return aId.localeCompare(bId, 'ja');
  };

  const mapEventForCalendar = (event) => {
    const { startTime, endTime, ...baseEvent } = event;
    const start = event.start;
    const end = event.end || event.start;
    const isAllDay = isDateOnlyValue(start) && isDateOnlyValue(end);

    if (!isAllDay) {
      return {
        ...baseEvent,
        allDay: false,
      };
    }

    return {
      ...baseEvent,
      allDay: true,
      end: new Date(new Date(end).getTime() + 24 * 60 * 60 * 1000)
        .toISOString()
        .split('T')[0],
    };
  };

  const handleDateClick = createDateClickHandler(calendarState);
  const handleEventClick = createEventClickHandler(calendarState, calendarEvents);
  const handleEventDrop = createEventDropHandler(calendarEvents);
  const handleViewChange = createViewChangeHandler(calendarState);
  const renderEventContent = createEventContentRenderer(calendarState.currentView);
  const storedView = sessionStorage.getItem('scheduleCalendarView');
  const initialView = isMobileOrTablet
    ? (storedView === 'dayGridMonth' || storedView === 'dayGridWeek' ? 'listWeek' : (storedView || 'listWeek'))
    : (storedView || 'dayGridMonth');

  // イベントが大量のときに毎レンダーで localeCompare を回さないようメモ化する
  const sortedFilteredEvents = React.useMemo(() => {
    const filtered = filterEvents(
      calendarEvents.events,
      calendarState.selectedCategories,
      calendarState.selectedAreas
    );
    return [...filtered].sort(compareByStartThenCreated);
    // compareByStartThenCreated はクロージャ内の純粋関数のため依存配列に含めない
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarEvents.events, calendarState.selectedCategories, calendarState.selectedAreas]);

  return (
    <>
      {/* スマートフォン用のハンバーガーメニューボタン */}
      {isMobileOrTablet && (
        <IconButton
          edge="start"
          color="inherit"
          aria-label="menu"
          onClick={calendarState.toggleDrawer(true)}
          sx={{ position: 'absolute', top: 8, right: 16, color: '#ffffff' }}
        >
          <MenuIcon />
        </IconButton>
      )}

      {/* メインエリア：カレンダー表示 */}
      <Box
        sx={{
          flex: 1,
          p: 2,
          maxWidth: 1680,
          width: '100%',   // 幅を100%に設定
          mx: 'auto',      // 中央寄せ
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
        inert={calendarState.open ? '' : undefined}
      >
        {/* カレンダー本体 */}
        <FullCalendar
          ref={calendarState.calendarRef}
          plugins={[dayGridPlugin, listPlugin, interactionPlugin]}
          initialView={initialView}
          initialDate={sessionStorage.getItem('calendarDate') || undefined}
          events={sortedFilteredEvents.map((event) => ({
            ...mapEventForCalendar(event),
            backgroundColor: event.category === 'プライベート'
              ? scheduleEventColors.privateBackground
              : scheduleEventColors.background,
            borderColor: event.category === 'プライベート'
              ? scheduleEventColors.privateBorder
              : scheduleEventColors.border,
          }))}
          eventOrder={(a, b) => compareByStartThenCreated(a, b)}
          eventDisplay="block"
          displayEventTime={false}
          eventTimeFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
          eventContent={renderEventContent}
          locale={jaLocale}
          dateClick={handleDateClick}
          eventClick={handleEventClick}
          eventDrop={handleEventDrop}
          editable={true}
          selectable={true}
          viewDidMount={handleViewChange}
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: isMobileOrTablet ? 'dayGridDay,listWeek' : 'dayGridMonth,dayGridWeek,dayGridDay,listWeek',
          }}
          datesSet={(info) => {
            const previousViewType = previousViewTypeRef.current;
            const currentViewType = info.view.type;
            const isViewModeChanged = previousViewType && previousViewType !== currentViewType;
            previousViewTypeRef.current = currentViewType;

            if (isViewModeChanged) {
              const today = new Date();
              const containsToday = today >= info.start && today < info.end;
              if (!containsToday) {
                info.view.calendar.gotoDate(today);
              }
            }

            calendarState.setCurrentView(info.view.type);
            sessionStorage.setItem('scheduleCalendarView', info.view.type);
            sessionStorage.setItem('calendarDate', info.view.currentStart.toISOString());
          }}
          dayHeaderContent={(args) => {
            if (isMobileOrTablet && calendarState.currentView === 'dayGridWeek') {
              const date = new Date(args.date);
              return date.getDate();
            }
            if (isMobileOrTablet && calendarState.currentView.startsWith('list')) {
              const date = new Date(args.date);
              const options = { weekday: 'short', day: 'numeric' };
              return date.toLocaleDateString('ja-JP', options);
            }
            return args.text;
          }}
        />
      </Box>
    </>
  );
};

export default CalendarMain;